<template>
  <v-container>
    <h3 class="text-h5 mb-2">
      {{ $t('admin.productsDatabase.importWizard.steps.price.title') }}
    </h3>
    <p class="text-body-1 mb-5">
      {{ $t('admin.productsDatabase.importWizard.steps.price.description') }}
    </p>

    <v-row>
      <v-col cols="6">
        <v-row>
          <v-col>
            <LocalBookCheckbox
              id="vat-included"
              :product-info-manager="productInfoManager"
              field-name="vatIncluded"
              :label="$t('admin.productsDatabase.importWizard.steps.price.vatIncluded')"
              @value="vatIncludedLocal = $event"
            />
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="7">
            <LocalBookTextInput
              id="book-price"
              :product-info-manager="productInfoManager"
              field-name="price"
              :label="$t('admin.productsDatabase.importWizard.steps.price.priceField')"
              placeholder="00.00"
              type="number"
              step="0.01"
              min="0"
              :rules="priceRules"
              @valid="handlePriceValidation"
            >
              <template #append-button="{ setValue }">
                <v-btn
                  color="primary"
                  small
                  class="ml-2 mb-6"
                  @click="setValue('0')"
                >
                  {{ $t('shop.free') }}
                </v-btn>
              </template>
            </LocalBookTextInput>
          </v-col>

          <v-col cols="5">
            <LocalBookCurrencySelect
              :product-info-manager="productInfoManager"
              field-name="currency"
              :label="$t('admin.productsDatabase.importWizard.steps.price.currencyField')"
            />
          </v-col>
        </v-row>
        <v-row v-if="vatIncludedLocal">
          <v-col cols="6">
            <div class="text-caption text--secondary mb-1">
              {{ $t('admin.productsDatabase.importWizard.steps.price.taxRegion') }}
            </div>
            <TaxRegionSelect
              ref="taxRegionSelect"
              v-model="selectedTaxRegion"
              :show-label="false"
              @input="handleTaxRegionChange"
            />
          </v-col>
          <v-col
            v-if="selectedTaxRate"
            cols="6"
          >
            <div class="text-caption text--secondary mb-1">
              {{ $t('admin.productsDatabase.importWizard.steps.price.vatRate') }}
            </div>
            <v-text-field
              :value="(selectedTaxRate * 100).toFixed(1) + '%'"
              readonly
              outlined
              dense
              hide-details
            />
          </v-col>
        </v-row>
        
        <v-row v-if="vatIncludedLocal">
          <v-col cols="12">
            <v-card
              outlined
              class="pa-3"
            >
              <v-card-title class="text-subtitle-1 pa-0 mb-2">
                {{ $t('admin.productsDatabase.importWizard.steps.price.priceBreakdown') }}
              </v-card-title>
              <v-row dense>
                <v-col cols="6">
                  <div class="text-caption text--secondary">
                    {{ $t('admin.productsDatabase.importWizard.steps.price.basePrice') }}
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ basePrice }}
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption text--secondary">
                    {{ $t('admin.productsDatabase.importWizard.steps.price.vatAmount') }}
                  </div>
                  <div class="text-body-1 font-weight-medium">
                    {{ formatCurrency(vatAmount) }}
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import currencyMixins from "@/mixins/currencyMixins";
import LocalBookTextInput from "@/components/LocalBookTextInput.vue";
import LocalBookCurrencySelect from "@/components/LocalBookCurrencySelect.vue";
import LocalBookCheckbox from "@/components/LocalBookCheckbox.vue";
import TaxRegionSelect from "@/components/inputs/TaxRegionSelect.vue";
import { MAX_PRICE } from '@/utils/Const';
import gql from 'graphql-tag';

export default {
  name: "PriceStep",
  components: {
    LocalBookTextInput,
    LocalBookCurrencySelect,
    LocalBookCheckbox,
    TaxRegionSelect
  },
  mixins: [currencyMixins],
  props: {
    productInfoManager: {
      type: Object,
      required: true
    }
  },
  emits: ['valid'],
  data: () => ({
    selectedTaxRegion: null,
    selectedTaxRate: null,
    priceInputValid: true, 
    vatIncludedLocal: false,
  }),
  apollo: {
    taxRates: {
      query: gql`
        query TaxRates {
          taxRates {
            id
            _id
            rate
          }
        }
      `,
    },
  },
  computed: {
    bookId() {
      return this.productInfoManager?.getBookId() || null;
    },
    productInfo() {
      return this.productInfoManager?.getData();
    },
    currentPrice() {
      const price = this.productInfo?.book?.price;
      return price !== undefined ? price : null;
    },
    currentVatIncluded() {
      return this.productInfo?.book?.vatIncluded || false;
    },
    currentCurrency() {
      return this.productInfo?.book?.currency || null;
    },
    priceRules() {
      return [
        (v) => {
          const num = parseFloat(v);
          return isNaN(num) || num >= 0 || 'Price cannot be negative';
        },
        (v) => {
          const num = parseFloat(v);
          return isNaN(num) || num <= MAX_PRICE || `Price cannot exceed ${MAX_PRICE.toLocaleString()}`;
        }
      ];
    },
    isValid() {
      if (!this.priceInputValid) {
        return false;
      }
      
      // Require price to be set (not null/undefined)
      if (this.currentPrice === null || this.currentPrice === undefined) {
        return false;
      }
      
      const num = parseFloat(this.currentPrice);
      if (num > 0 && !this.currentCurrency) {
        return false;
      }
      
      return true;
    },
    rate() {
      return Number(this.selectedTaxRate) || 0;
    },
    basePrice() {
      if (!this.currentVatIncluded || !this.selectedTaxRate || !this.currentPrice) {
        return parseFloat(this.currentPrice) || 0;
      }
      return parseFloat(this.currentPrice) / (1 + this.rate);
    },
    vatAmount() {
      if (!this.currentVatIncluded || !this.selectedTaxRate || !this.currentPrice) {
        return 0;
      }
      return parseFloat(this.currentPrice) - this.basePrice;
    },
  },
  watch: {
    isValid(newValid) {
      this.$emit('valid', newValid);
    }
  },
  mounted() {
    // Emit initial validation state
    this.$emit('valid', this.isValid);
  },
  methods: {
    handlePriceValidation(isValid) {
      this.priceInputValid = isValid;
    },
    
    handleTaxRegionChange(taxRegionId) {
      this.selectedTaxRegion = taxRegionId;
      
      // Find the tax rate from our local Apollo data
      if (taxRegionId && this.taxRates) {
        const taxRate = this.taxRates.find(rate => rate.id === taxRegionId);
        this.selectedTaxRate = taxRate ? taxRate.rate : null;
      } else {
        this.selectedTaxRate = null;
      }
    },
    
    formatCurrency(amount) {
      // Use the currency mixin if available, otherwise format as simple number
      if (this.formatMoney) {
        return this.formatMoney(amount);
      }
      return amount.toFixed(2);
    },
    
  }
};
</script>

<style scoped>
/* Add any specific styles here if needed */
</style>